import { Injectable, inject } from '@angular/core';

import { DateService } from './date.service';
import { DateRange, StateService } from './state.service';
import { MetricService } from './metric.service';
import { Institution } from './institution.service';

export type DataSeriesPeer = { dates: Date[], dateSeries: string[], institutionSeries: (number | null)[], peerSeries: (number | null)[] };
export type PeerInstitutionSeries = { institution: Institution, series: (number | null)[] };
export type DataSeriesInstitutions = { dateSeries: string[], institutionSeries: (number | null)[], peerInstitutionSeries: PeerInstitutionSeries[] };
export type DataValuesPeer = { date: Date, institutionValue: number | null, peerValue: number | null, minValue: number, maxValue: number };

export interface ChartColor {
  metricId: string;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class VisualService {
  private dateService = inject(DateService);
  private metricService = inject(MetricService);

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  constructor() { }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricSeriesPeer(displayType: string | undefined = '', data: any[], dateRange: DateRange | null = null, currentProduct?: string): DataSeriesPeer {
    dateRange ??= this.dateRange() ?? {} as DateRange;
    const dateSeries = this.dateService.getDateSeries(this.institution().institutionType, dateRange, undefined, currentProduct);
    const indexes: number[] = this.getPeerIndexes(data[0]);

    const institutionIndex = data[0].findIndex((c: number) => { return c == this.institution().iid; });
    const institutionData: (number | null)[] = [];
    const peerData: (number | null)[] = [];

    data.forEach((row) => {
      const dataRow = dateSeries.dates.findIndex((d: Date) => d.getTime() == new Date(row[0]).getTime());
      if (dataRow > -1) {
        let sum: number | null = null; let count = 0;
        indexes.forEach(i => {
          if (row[i] != null) {
            sum += row[i]; count++;
          }
        });
        const value: number | null = (sum != null && count > 0) ? sum / count : null;

        institutionData[dataRow] = VisualService.getValue(row[institutionIndex], displayType);
        if (value != null) {
          peerData[dataRow] = VisualService.getValue(value, displayType);
        }
      }
    });

    return { dates: dateSeries.dates, dateSeries: dateSeries.labels, institutionSeries: institutionData, peerSeries: peerData };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricSeriesInstitutions(displayType: string | undefined = '', data: any[], peerInstitutions: Institution[], dateRange: DateRange | null = null): DataSeriesInstitutions {
    dateRange ??= this.dateRange() ?? {} as DateRange;
    const dateSeries = this.dateService.getDateSeries(this.institution().institutionType, dateRange);

    const institutionIndex = data[0].findIndex((c: number) => { return c == this.institution().iid; });
    const institutionData: (number | null)[] = [];
    const peerInstitutionSeries = peerInstitutions.map((institution) => { return { institution: institution, series: [] as (number | null)[] }; });

    data.forEach((row) => {
      const dataRow = dateSeries.dates.findIndex((d: Date) => d.getTime() == new Date(row[0]).getTime());
      if (dataRow > -1) {
        institutionData[dataRow] = VisualService.getValue(row[institutionIndex], displayType);
        peerInstitutionSeries.forEach((institutionSeries) => {
          const peerIndex = data[0].findIndex((c: number) => { return c == institutionSeries.institution.iid; });
          if (peerIndex > -1) {
            const value = row[peerIndex];
            institutionSeries.series[dataRow] = VisualService.getValue(value, displayType);
          }
        });
      }
    });

    return { dateSeries: dateSeries.labels, institutionSeries: institutionData, peerInstitutionSeries: peerInstitutionSeries };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricValuesPeer(displayType: string | undefined = '', data: any[], date: Date): DataValuesPeer {
    const indexes: number[] = this.getPeerIndexes(data[0]);

    const institutionIndex = data[0].findIndex((c: number) => { return c == this.institution().iid; });
    let institutionValue: number | null = null;
    let peerValue: number | null = null;
    let minValue = -1;
    let maxValue = -1;

    const dates = data.map((row) => new Date(row[0]));
    const dataRow = dates.findIndex((d: Date) => new Date(d).getTime() == date.getTime());
    if (dataRow > -1) {
      let sum: number | null = null; let count = 0;
      indexes.forEach(i => {
        const value = data[dataRow][i];
        if (value != null) {
          sum += value; count++;
          if (minValue == -1 || value < minValue) minValue = value;
          if (maxValue == -1 || value > maxValue) maxValue = value;
        }
      });
      const value: number | null = (sum != null && count > 0) ? sum / count : null;

      //institutionValue = VisualService.getValue(data[dataRow][institutionIndex], displayType);
      //peerValue = value != null ? VisualService.getValue(value, displayType) : null;
      //minValue = VisualService.getValue(minValue, displayType) ?? 0;
      //maxValue = VisualService.getValue(maxValue, displayType) ?? 0;

      institutionValue = data[dataRow][institutionIndex];
      peerValue = value;

      if (institutionValue) {
        if (institutionValue < minValue) minValue = institutionValue;
        if (institutionValue > maxValue) maxValue = institutionValue;
      }
    }

    return { date: date, institutionValue: institutionValue, peerValue: peerValue, minValue: minValue, maxValue: maxValue };
  }

  public static getValue(value: number | null, displayType: string | undefined = '', usdDivisor = 1000, pctDivisor = 1, round: boolean = true): number | null {
    if (value == null) return null;
    switch (displayType) {
      case 'USD':
        return round ? Math.round(value / usdDivisor) : value / usdDivisor;
      case 'USDM':
        return round ? Math.round(value / 1000) : value / 1000;
      case 'USDMDecimal':
        return value / 1000;
      case 'USDMM':
        return round ? Math.round(value / 1000000) : value / 1000000;
      case 'PCT':
        return Number((value / pctDivisor).toFixed(2));
      case 'RATIO':
        return Number(value.toFixed(2));
      case 'INT':
      case 'INTEGER':
      case 'ORDINAL':
        return round ? Math.round(value) : value;
      default:
        return value;
    }
  }

  public static getValueDisplay(value: number, displayType: string | undefined = '', round: boolean = true): string {
    switch (displayType) {
      case 'USD':
      case 'USDM':
        return round ? value.toLocaleString() + 'M' : value.toFixed(2) + 'M';
      case 'USDMM':
        return round ? value.toLocaleString() + 'MM' : value.toFixed(2) + 'MM';
      case 'PCT':
        return value.toFixed(2) + '%';
      case 'PCT0':
        return value.toFixed(0) + '%';
      case 'PCT1':
        return value.toFixed(1) + '%';
      case 'PCT2':
        return value.toFixed(2) + '%';
      case 'PCT3':
        return value.toFixed(3) + '%';
      case 'PCT4':
        return value.toFixed(4) + '%';
      case 'DEC':
        return value.toFixed(2);
      case 'DEC0':
        return value.toFixed(0);
      case 'DEC1':
        return value.toFixed(1);
      case 'DEC2':
        return value.toFixed(2);
      case 'DEC3':
        return value.toFixed(3);
      case 'DEC4':
        return value.toFixed(4);
      case 'RATIO':
        return value.toFixed(2);
      case 'INT':
      case 'INTEGER':
        return value.toLocaleString();
      case 'ORDINAL':
        return value.toLocaleString() + this.getOrdinalSuffix(value);
      case 'TRILLION':
        return value.toLocaleString() + 'T';

      default:
        return value.toLocaleString();
    }
  }

  public static getValueDisplayOrNull(value: number | null, displayType: string | undefined = '', round: boolean = true): string | null {
    if (value == null) return null;
    return VisualService.getValueDisplay(value, displayType, round);
  }

  public getPeerIndexes(data: number[]): number[] {
    const indexes: number[] = [];
    if (this.peer()?.group) {
      if (this.peer()?.group?.groupType == 30) {
        const index = data.findIndex((c: number) => { return c < 0; });
        if (index > -1) indexes.push(index);
      } else {
        this.filteredGroup().forEach((groupInstitution) => {
          if (groupInstitution.isSelected) {
            const index = data.indexOf(groupInstitution.iid);
            if (index > -1) indexes.push(index);
          }
        });
      }
    }
    else {
      indexes.push(data.findIndex((c: number) => {
        return c == this.peer()?.institution?.iid;
      }));
    }
    return indexes;
  }

  public static getOrdinalSuffix(value: number): string {
    if (value > 3 && value < 21) return 'th';
    switch (value % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }
}
